const numeric=value=>Number(value)||0;

export function buildStepPreviewScene(mesh,rotation={x:-.45,y:.65},{autoOrient=false}={}) {
  const vertices=mesh?.vertices||[],triangles=mesh?.triangles||[];
  if(!vertices.length||!triangles.length)return{faces:[],edges:[]};
  const xs=vertices.map(vertex=>numeric(vertex[0])),ys=vertices.map(vertex=>numeric(vertex[1])),zs=vertices.map(vertex=>numeric(vertex[2]));
  const center=[(Math.min(...xs)+Math.max(...xs))/2,(Math.min(...ys)+Math.max(...ys))/2,(Math.min(...zs)+Math.max(...zs))/2];
  const cy=Math.cos(rotation.y),sy=Math.sin(rotation.y),cx=Math.cos(rotation.x),sx=Math.sin(rotation.x);
  let points=vertices.map(vertex=>{
    const x=numeric(vertex[0])-center[0],y=numeric(vertex[1])-center[1],z=numeric(vertex[2])-center[2];
    const x1=x*cy+z*sy,z1=-x*sy+z*cy,y1=y*cx-z1*sx,z2=y*sx+z1*cx;
    return{x:x1,y:y1,z:z2};
  });
  if(autoOrient){
    const width=Math.max(...points.map(p=>p.x))-Math.min(...points.map(p=>p.x));
    const height=Math.max(...points.map(p=>p.y))-Math.min(...points.map(p=>p.y));
    // Camera roll for tall thumbnail projections only; source vertices, depth,
    // fold data and manufacturing orientation are never changed.
    if(height>width*1.5)points=points.map(p=>({...p,x:p.y,y:-p.x}));
  }
  const extent=Math.max(1,...points.flatMap(point=>[Math.abs(point.x),Math.abs(point.y)])),scale=128/extent;
  const screen=points.map(point=>({x:160+point.x*scale,y:155-point.y*scale}));
  const welded=vertices.map(vertex=>vertex.map(value=>Math.round(numeric(value)*10000)/10000).join(','));
  const light={x:.35,y:-.45,z:.82},edgeMap=new Map();
  const faces=triangles.map((triangle,index)=>{
    const p=triangle.map(vertex=>points[vertex]),a={x:p[1].x-p[0].x,y:p[1].y-p[0].y,z:p[1].z-p[0].z},b={x:p[2].x-p[0].x,y:p[2].y-p[0].y,z:p[2].z-p[0].z};
    const cross={x:a.y*b.z-a.z*b.y,y:a.z*b.x-a.x*b.z,z:a.x*b.y-a.y*b.x},length=Math.hypot(cross.x,cross.y,cross.z)||1;
    const rawNormal={x:cross.x/length,y:cross.y/length,z:cross.z/length},normal=rawNormal.z<0?{x:-rawNormal.x,y:-rawNormal.y,z:-rawNormal.z}:rawNormal;
    const diffuse=Math.max(0,normal.x*light.x+normal.y*light.y+normal.z*light.z),rim=1-Math.min(1,Math.abs(normal.z)),brightness=Math.min(1,.34+diffuse*.54+rim*.12);
    const tone=Math.round(122+brightness*112),fill=`rgb(${Math.min(238,tone+4)},${Math.min(240,tone+6)},${Math.min(242,tone+8)})`;
    for(const [from,to] of [[triangle[0],triangle[1]],[triangle[1],triangle[2]],[triangle[2],triangle[0]]]){
      const key=[welded[from],welded[to]].sort().join('|'),entry=edgeMap.get(key)||{key,from,to,normals:[]};
      entry.normals.push(rawNormal);edgeMap.set(key,entry);
    }
    return{index,z:(p[0].z+p[1].z+p[2].z)/3,points:triangle.map(vertex=>`${screen[vertex].x},${screen[vertex].y}`).join(' '),fill};
  }).sort((a,b)=>a.z-b.z);
  const sharpCos=Math.cos(24*Math.PI/180),edges=[];
  for(const entry of edgeMap.values()){
    const front=entry.normals.some(normal=>normal.z>.025),boundary=entry.normals.length===1&&front;
    const silhouette=entry.normals.length>1&&entry.normals.some(normal=>normal.z>.025)&&entry.normals.some(normal=>normal.z<-.025);
    const sharp=entry.normals.length>1&&front&&entry.normals.some((normal,index)=>entry.normals.slice(index+1).some(other=>Math.abs(normal.x*other.x+normal.y*other.y+normal.z*other.z)<sharpCos));
    if(!boundary&&!silhouette&&!sharp)continue;
    const from=screen[entry.from],to=screen[entry.to];
    edges.push({key:entry.key,x1:from.x,y1:from.y,x2:to.x,y2:to.y,strong:boundary||silhouette});
  }
  return{faces,edges};
}

export function stepPreviewIsOpaque(scene) {
  return !!scene?.faces?.length&&scene.faces.every(face=>/^rgb\(\d+,\d+,\d+\)$/.test(face.fill));
}
